import { FieldDef, GroupDef, GuiDef, ValidatorDef, TabDefinition, CollapsableDef } from '@/objects/gui';
import { Category } from '@/objects/generated';
import { markRaw, computed } from 'vue';
import {
    InputText,
    ValidatedText,
    InputSelect,
    DataSourceLookup,
    InputNullableInt,
    ValidatedInt,
    DisplayInfotext,
    InputNullableFloat,
    ValidatedFloat,
    InputNullableTimespan,
    DisplayTimespan,
    DisplaySeparator,
    InputInt,
    InputBool,
    DisplayBool,
    InputNullableDateTime,
    DateComp,
} from '@/components';
import { useCommonStore } from '@/stores';
import { storeToRefs } from 'pinia';
import { type DateType } from '@/utilities/dateutils';
import type { SelectLine, SelectLineValueType } from '@/objects/SelectLine';
import { useValidator } from '@/composeables';

export class CategoryGuiDef extends GuiDef<Category> {
    constructor() {
        super('Category');

        const Common = storeToRefs(useCommonStore());
        const GendersSource = computed(() => {
            return Common.GendersAsSelectLine.value;
        });
        const PrizeShareTypesSource = computed(() => {
            return Common.PrizeShareTypesAsSelectLine.value;
        });

        const NameValidator = new ValidatorDef({ Min: null, Max: null, Required: true });
        const Name = new FieldDef<Category>();
        Name.Collapsable = new CollapsableDef('advanced', 'contestant.advanced');
        Name.Name = 'Name';
        Name.FieldName = 'Name';
        Name.DefinedFor = 'List';
        Name.TranslationKey = 'category.name';
        Name.Editable = true;
        Name.Filterable = true;
        Name.Sortable = true;
        Name.CssClasses = '';
        Name.value = (category) => category.Name;
        Name.setValue = (category, value) => (category.Name = value);
        Name.DetailEditor = markRaw(InputText);
        Name.DetailEditorProps = {};
        Name.CellEditor = markRaw(InputText);
        Name.CellEditorProps = {};
        Name.CellRenderer = markRaw(ValidatedText);
        Name.CellRendererProps = {};
        Name.Validator = new ValidatorDef({ Min: null, Max: null, Required: true });
        Name.SortDirection = undefined;
        Name.cellClassRules = [
            {
                className: 'grid-cell-warning',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(NameValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'warning';
                },
            },
            {
                className: 'grid-cell-error',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(NameValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'danger';
                },
            },
        ];
        Name.MinWidth = undefined;
        Name.MaxWidth = undefined;
        Name.Tooltip = null;
        this.Fields.push(Name);

        const Gender = new FieldDef<Category>();
        Gender.Collapsable = new CollapsableDef('advanced', 'contestant.advanced');
        Gender.Name = 'Gender';
        Gender.FieldName = 'Gender';
        Gender.DefinedFor = 'List';
        Gender.TranslationKey = 'category.gender';
        Gender.Editable = true;
        Gender.Filterable = true;
        Gender.Sortable = true;
        Gender.CssClasses = '';
        Gender.value = (category) => category.Gender;
        Gender.setValue = (category, value) => (category.Gender = value);
        Gender.DetailEditor = markRaw(InputSelect);
        Gender.DetailEditorProps = { datasource: { mapsource: GendersSource } };
        Gender.CellEditor = markRaw(InputSelect);
        Gender.CellEditorProps = { datasource: { mapsource: GendersSource } };
        // @ts-expect-error @ts-ignore
        Gender.CellRenderer = markRaw(DataSourceLookup);
        Gender.CellRendererProps = { datasource: { mapsource: GendersSource } };
        Gender.ExportValueFormatter = (obj: Category) => {
            return GendersSource.value.get(obj.Gender)?.text;
        };
        Gender.Validator = null;
        Gender.SortDirection = undefined;
        Gender.cellClassRules = undefined;
        Gender.MinWidth = undefined;
        Gender.MaxWidth = undefined;
        Gender.Tooltip = null;
        this.Fields.push(Gender);

        const AgeFromValidator = new ValidatorDef({ Min: 0, Max: 200, Required: true });
        const AgeFrom = new FieldDef<Category>();
        AgeFrom.Group = new GroupDef('category.agefromto', '-', true);
        AgeFrom.Collapsable = new CollapsableDef('advanced', 'contestant.advanced');
        AgeFrom.Name = 'AgeFrom';
        AgeFrom.FieldName = 'AgeFrom';
        AgeFrom.DefinedFor = 'List';
        AgeFrom.TranslationKey = 'category.agefrom';
        AgeFrom.Editable = true;
        AgeFrom.Filterable = false;
        AgeFrom.Sortable = true;
        AgeFrom.CssClasses = '';
        AgeFrom.value = (category) => category.AgeFrom;
        AgeFrom.setValue = (category, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            category.AgeFrom = value;
        };
        AgeFrom.DetailEditor = markRaw(InputNullableInt);
        AgeFrom.DetailEditorProps = {};
        AgeFrom.CellEditor = markRaw(InputNullableInt);
        AgeFrom.CellEditorProps = {};
        AgeFrom.CellRenderer = markRaw(ValidatedInt);
        AgeFrom.CellRendererProps = {};
        AgeFrom.Validator = new ValidatorDef({ Min: 0, Max: 200, Required: true });
        AgeFrom.SortDirection = undefined;
        AgeFrom.cellClassRules = [
            {
                className: 'grid-cell-warning',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(AgeFromValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'warning';
                },
            },
            {
                className: 'grid-cell-error',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(AgeFromValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'danger';
                },
            },
        ];
        AgeFrom.MinWidth = undefined;
        AgeFrom.MaxWidth = undefined;
        AgeFrom.Tooltip = null;
        this.Fields.push(AgeFrom);

        const AgeToValidator = new ValidatorDef({ Min: 1, Max: 200, Required: true });
        const AgeTo = new FieldDef<Category>();
        AgeTo.Group = new GroupDef('category.agefromto', '-', true);
        AgeTo.Collapsable = new CollapsableDef('advanced', 'contestant.advanced');
        AgeTo.Name = 'AgeTo';
        AgeTo.FieldName = 'AgeTo';
        AgeTo.DefinedFor = 'List';
        AgeTo.TranslationKey = 'category.ageto';
        AgeTo.Editable = true;
        AgeTo.Filterable = false;
        AgeTo.Sortable = true;
        AgeTo.CssClasses = '';
        AgeTo.value = (category) => category.AgeTo;
        AgeTo.setValue = (category, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            category.AgeTo = value;
        };
        AgeTo.DetailEditor = markRaw(InputNullableInt);
        AgeTo.DetailEditorProps = {};
        AgeTo.CellEditor = markRaw(InputNullableInt);
        AgeTo.CellEditorProps = {};
        AgeTo.CellRenderer = markRaw(ValidatedInt);
        AgeTo.CellRendererProps = {};
        AgeTo.Validator = new ValidatorDef({ Min: 1, Max: 200, Required: true });
        AgeTo.SortDirection = undefined;
        AgeTo.cellClassRules = [
            {
                className: 'grid-cell-warning',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(AgeToValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'warning';
                },
            },
            {
                className: 'grid-cell-error',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(AgeToValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'danger';
                },
            },
        ];
        AgeTo.MinWidth = undefined;
        AgeTo.MaxWidth = undefined;
        AgeTo.Tooltip = null;
        this.Fields.push(AgeTo);

        const Sort = new FieldDef<Category>();
        Sort.Group = new GroupDef('row10', '', true);
        Sort.Collapsable = new CollapsableDef('advanced', 'contestant.advanced');
        Sort.Name = 'Sort';
        Sort.FieldName = 'Sort';
        Sort.DefinedFor = 'Both';
        Sort.TranslationKey = 'category.sort';
        Sort.Editable = true;
        Sort.Filterable = false;
        Sort.Sortable = true;
        Sort.CssClasses = '';
        Sort.value = (category) => category.Sort;
        Sort.setValue = (category, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            category.Sort = value;
        };
        Sort.DetailEditor = markRaw(InputNullableInt);
        Sort.DetailEditorProps = {};
        Sort.CellEditor = markRaw(InputNullableInt);
        Sort.CellEditorProps = {};
        Sort.CellRenderer = markRaw(ValidatedInt);
        Sort.CellRendererProps = {};
        Sort.Validator = null;
        Sort.SortDirection = undefined;
        Sort.cellClassRules = undefined;
        Sort.MinWidth = undefined;
        Sort.MaxWidth = undefined;
        Sort.Tooltip = null;
        this.Fields.push(Sort);

        const infotext0 = new FieldDef<Category>();
        infotext0.Group = new GroupDef('row10', '', true);
        infotext0.Collapsable = new CollapsableDef('advanced', 'contestant.advanced');
        infotext0.Name = 'infotext0';
        infotext0.FieldName = 'infotext0';
        infotext0.DefinedFor = 'Detail';
        infotext0.TranslationKey = '';
        infotext0.Editable = true;
        infotext0.Filterable = false;
        infotext0.Sortable = true;
        infotext0.CssClasses = '';
        infotext0.value = () => {
            return '';
        };
        infotext0.setValue = () => {};
        infotext0.DetailEditor = markRaw(DisplayInfotext);
        infotext0.DetailEditorProps = {};
        infotext0.Validator = null;
        infotext0.SortDirection = undefined;
        infotext0.cellClassRules = undefined;
        infotext0.MinWidth = undefined;
        infotext0.MaxWidth = undefined;
        infotext0.Tooltip = null;
        this.Fields.push(infotext0);

        const infotext1 = new FieldDef<Category>();
        infotext1.Group = new GroupDef('row10', '', true);
        infotext1.Collapsable = new CollapsableDef('advanced', 'contestant.advanced');
        infotext1.Name = 'infotext1';
        infotext1.FieldName = 'infotext1';
        infotext1.DefinedFor = 'Detail';
        infotext1.TranslationKey = '';
        infotext1.Editable = true;
        infotext1.Filterable = false;
        infotext1.Sortable = true;
        infotext1.CssClasses = '';
        infotext1.value = () => {
            return '';
        };
        infotext1.setValue = () => {};
        infotext1.DetailEditor = markRaw(DisplayInfotext);
        infotext1.DetailEditorProps = {};
        infotext1.Validator = null;
        infotext1.SortDirection = undefined;
        infotext1.cellClassRules = undefined;
        infotext1.MinWidth = undefined;
        infotext1.MaxWidth = undefined;
        infotext1.Tooltip = null;
        this.Fields.push(infotext1);

        const infotext2 = new FieldDef<Category>();
        infotext2.Group = new GroupDef('row10', '', true);
        infotext2.Collapsable = new CollapsableDef('advanced', 'contestant.advanced');
        infotext2.Name = 'infotext2';
        infotext2.FieldName = 'infotext2';
        infotext2.DefinedFor = 'Detail';
        infotext2.TranslationKey = '';
        infotext2.Editable = true;
        infotext2.Filterable = false;
        infotext2.Sortable = true;
        infotext2.CssClasses = '';
        infotext2.value = () => {
            return '';
        };
        infotext2.setValue = () => {};
        infotext2.DetailEditor = markRaw(DisplayInfotext);
        infotext2.DetailEditorProps = {};
        infotext2.Validator = null;
        infotext2.SortDirection = undefined;
        infotext2.cellClassRules = undefined;
        infotext2.MinWidth = undefined;
        infotext2.MaxWidth = undefined;
        infotext2.Tooltip = null;
        this.Fields.push(infotext2);

        const PrizeShare = new FieldDef<Category>();
        PrizeShare.Group = new GroupDef('category.numprizes', '', true);
        PrizeShare.Collapsable = new CollapsableDef('advanced', 'contestant.advanced');
        PrizeShare.Name = 'PrizeShare';
        PrizeShare.FieldName = 'PrizeShare';
        PrizeShare.DefinedFor = 'Both';
        PrizeShare.TranslationKey = 'category.prizeshare';
        PrizeShare.Editable = true;
        PrizeShare.Filterable = true;
        PrizeShare.Sortable = true;
        PrizeShare.CssClasses = '';
        PrizeShare.value = (category) => category.PrizeShare;
        PrizeShare.setValue = (category, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            category.PrizeShare = value;
        };
        PrizeShare.DetailEditor = markRaw(InputNullableFloat);
        PrizeShare.DetailEditorProps = { decimal: 2 };
        PrizeShare.CellEditor = markRaw(InputNullableFloat);
        PrizeShare.CellEditorProps = { decimal: 2 };
        PrizeShare.CellRenderer = markRaw(ValidatedFloat);
        PrizeShare.CellRendererProps = { decimal: 2 };
        PrizeShare.Validator = null;
        PrizeShare.SortDirection = undefined;
        PrizeShare.cellClassRules = undefined;
        PrizeShare.MinWidth = undefined;
        PrizeShare.MaxWidth = undefined;
        PrizeShare.Tooltip = null;
        this.Fields.push(PrizeShare);

        const PrizeShareType = new FieldDef<Category>();
        PrizeShareType.Group = new GroupDef('category.numprizes', '', true);
        PrizeShareType.Collapsable = new CollapsableDef('advanced', 'contestant.advanced');
        PrizeShareType.Name = 'PrizeShareType';
        PrizeShareType.FieldName = 'PrizeShareType';
        PrizeShareType.DefinedFor = 'Both';
        PrizeShareType.TranslationKey = 'category.prizesharetype';
        PrizeShareType.Editable = true;
        PrizeShareType.Filterable = true;
        PrizeShareType.Sortable = true;
        PrizeShareType.CssClasses = '';
        PrizeShareType.value = (category) => category.PrizeShareType;
        PrizeShareType.setValue = (category, value) => (category.PrizeShareType = value);
        PrizeShareType.DetailEditor = markRaw(InputSelect);
        PrizeShareType.DetailEditorProps = { datasource: { mapsource: PrizeShareTypesSource } };
        PrizeShareType.CellEditor = markRaw(InputSelect);
        PrizeShareType.CellEditorProps = { datasource: { mapsource: PrizeShareTypesSource } };
        // @ts-expect-error @ts-ignore
        PrizeShareType.CellRenderer = markRaw(DataSourceLookup);
        PrizeShareType.CellRendererProps = { datasource: { mapsource: PrizeShareTypesSource } };
        PrizeShareType.ExportValueFormatter = (obj: Category) => {
            return PrizeShareTypesSource.value.get(obj.PrizeShareType)?.text;
        };
        PrizeShareType.Validator = null;
        PrizeShareType.SortDirection = undefined;
        PrizeShareType.cellClassRules = undefined;
        PrizeShareType.MinWidth = undefined;
        PrizeShareType.MaxWidth = undefined;
        PrizeShareType.Tooltip = null;
        this.Fields.push(PrizeShareType);

        const ShootingPenalty = new FieldDef<Category>();
        ShootingPenalty.Collapsable = new CollapsableDef('advanced', 'contestant.advanced');
        ShootingPenalty.Name = 'ShootingPenalty';
        ShootingPenalty.FieldName = 'ShootingPenalty';
        ShootingPenalty.DefinedFor = 'Both';
        ShootingPenalty.TranslationKey = 'category.shootingpenalty';
        ShootingPenalty.Editable = true;
        ShootingPenalty.Filterable = true;
        ShootingPenalty.Sortable = true;
        ShootingPenalty.CssClasses = '';
        ShootingPenalty.value = (category) => category.ShootingPenalty;
        ShootingPenalty.setValue = (category, value) => (category.ShootingPenalty = value);
        ShootingPenalty.DetailEditor = markRaw(InputNullableTimespan);
        ShootingPenalty.DetailEditorProps = { format: 'TIME' as DateType };
        ShootingPenalty.CellEditor = markRaw(InputNullableTimespan);
        ShootingPenalty.CellEditorProps = { format: 'TIME' as DateType };
        ShootingPenalty.CellRenderer = markRaw(DisplayTimespan);
        ShootingPenalty.CellRendererProps = { format: 'TIME' as DateType };
        ShootingPenalty.Validator = null;
        ShootingPenalty.SortDirection = undefined;
        ShootingPenalty.cellClassRules = undefined;
        ShootingPenalty.MinWidth = 100;
        ShootingPenalty.MaxWidth = undefined;
        ShootingPenalty.Tooltip = null;
        this.Fields.push(ShootingPenalty);

        const separator3 = new FieldDef<Category>();
        separator3.Collapsable = new CollapsableDef('advanced', 'contestant.advanced');
        separator3.Name = 'separator3';
        separator3.FieldName = 'separator3';
        separator3.DefinedFor = 'Detail';
        separator3.TranslationKey = 'category.bibsettings';
        separator3.Editable = true;
        separator3.Filterable = false;
        separator3.Sortable = true;
        separator3.CssClasses = '';
        separator3.value = () => {
            return 'category.bibsettings';
        };
        separator3.setValue = () => {};
        separator3.DetailEditor = markRaw(DisplaySeparator);
        separator3.DetailEditorProps = {};
        separator3.CellEditor = markRaw(DisplaySeparator);
        separator3.CellEditorProps = {};
        separator3.CellRenderer = markRaw(DisplaySeparator);
        separator3.CellRendererProps = {};
        separator3.Validator = null;
        separator3.SortDirection = undefined;
        separator3.cellClassRules = undefined;
        separator3.MinWidth = undefined;
        separator3.MaxWidth = undefined;
        separator3.Tooltip = null;
        this.Fields.push(separator3);

        const BibFrom = new FieldDef<Category>();
        BibFrom.Group = new GroupDef('category.bibfromto', '-', true);
        BibFrom.Collapsable = new CollapsableDef('advanced', 'contestant.advanced');
        BibFrom.Name = 'BibFrom';
        BibFrom.FieldName = 'BibFrom';
        BibFrom.DefinedFor = 'Both';
        BibFrom.TranslationKey = 'category.bibfrom';
        BibFrom.Editable = true;
        BibFrom.Filterable = false;
        BibFrom.Sortable = true;
        BibFrom.CssClasses = '';
        BibFrom.value = (category) => category.BibFrom;
        BibFrom.setValue = (category, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            category.BibFrom = value;
        };
        BibFrom.DetailEditor = markRaw(InputNullableInt);
        BibFrom.DetailEditorProps = {};
        BibFrom.CellEditor = markRaw(InputNullableInt);
        BibFrom.CellEditorProps = {};
        BibFrom.CellRenderer = markRaw(ValidatedInt);
        BibFrom.CellRendererProps = {};
        BibFrom.Validator = null;
        BibFrom.SortDirection = undefined;
        BibFrom.cellClassRules = undefined;
        BibFrom.MinWidth = undefined;
        BibFrom.MaxWidth = undefined;
        BibFrom.Tooltip = null;
        this.Fields.push(BibFrom);

        const BibTo = new FieldDef<Category>();
        BibTo.Group = new GroupDef('category.bibfromto', '-', true);
        BibTo.Collapsable = new CollapsableDef('advanced', 'contestant.advanced');
        BibTo.Name = 'BibTo';
        BibTo.FieldName = 'BibTo';
        BibTo.DefinedFor = 'Both';
        BibTo.TranslationKey = 'category.bibto';
        BibTo.Editable = true;
        BibTo.Filterable = false;
        BibTo.Sortable = true;
        BibTo.CssClasses = '';
        BibTo.value = (category) => category.BibTo;
        BibTo.setValue = (category, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            category.BibTo = value;
        };
        BibTo.DetailEditor = markRaw(InputNullableInt);
        BibTo.DetailEditorProps = {};
        BibTo.CellEditor = markRaw(InputNullableInt);
        BibTo.CellEditorProps = {};
        BibTo.CellRenderer = markRaw(ValidatedInt);
        BibTo.CellRendererProps = {};
        BibTo.Validator = null;
        BibTo.SortDirection = undefined;
        BibTo.cellClassRules = undefined;
        BibTo.MinWidth = undefined;
        BibTo.MaxWidth = undefined;
        BibTo.Tooltip = null;
        this.Fields.push(BibTo);

        const ReserveBibs = new FieldDef<Category>();
        ReserveBibs.Group = new GroupDef('row30', '', true);
        ReserveBibs.Collapsable = new CollapsableDef('advanced', 'contestant.advanced');
        ReserveBibs.Name = 'ReserveBibs';
        ReserveBibs.FieldName = 'ReserveBibs';
        ReserveBibs.DefinedFor = 'Both';
        ReserveBibs.TranslationKey = 'category.reservebibs';
        ReserveBibs.Editable = true;
        ReserveBibs.Filterable = false;
        ReserveBibs.Sortable = true;
        ReserveBibs.CssClasses = '';
        ReserveBibs.value = (category) => category.ReserveBibs;
        ReserveBibs.setValue = (category, value) => {
            if (value === null || value === undefined || value === '' || isNaN(value)) {
                value = 0;
            }
            category.ReserveBibs = value;
        };
        ReserveBibs.DetailEditor = markRaw(InputInt);
        ReserveBibs.DetailEditorProps = {};
        ReserveBibs.CellEditor = markRaw(InputInt);
        ReserveBibs.CellEditorProps = {};
        ReserveBibs.CellRenderer = markRaw(ValidatedInt);
        ReserveBibs.CellRendererProps = {};
        ReserveBibs.Validator = null;
        ReserveBibs.SortDirection = undefined;
        ReserveBibs.cellClassRules = undefined;
        ReserveBibs.MinWidth = undefined;
        ReserveBibs.MaxWidth = undefined;
        ReserveBibs.Tooltip = 'category.reservebibstooltip';
        this.Fields.push(ReserveBibs);

        const infotext4 = new FieldDef<Category>();
        infotext4.Group = new GroupDef('row30', '', true);
        infotext4.Collapsable = new CollapsableDef('advanced', 'contestant.advanced');
        infotext4.Name = 'infotext4';
        infotext4.FieldName = 'infotext4';
        infotext4.DefinedFor = 'Detail';
        infotext4.TranslationKey = '';
        infotext4.Editable = true;
        infotext4.Filterable = false;
        infotext4.Sortable = true;
        infotext4.CssClasses = '';
        infotext4.value = () => {
            return '';
        };
        infotext4.setValue = () => {};
        infotext4.DetailEditor = markRaw(DisplayInfotext);
        infotext4.DetailEditorProps = {};
        infotext4.Validator = null;
        infotext4.SortDirection = undefined;
        infotext4.cellClassRules = undefined;
        infotext4.MinWidth = undefined;
        infotext4.MaxWidth = undefined;
        infotext4.Tooltip = null;
        this.Fields.push(infotext4);

        const ReserveBibsInFront = new FieldDef<Category>();
        ReserveBibsInFront.Group = new GroupDef('row32', '', true);
        ReserveBibsInFront.Collapsable = new CollapsableDef('advanced', 'contestant.advanced');
        ReserveBibsInFront.Name = 'ReserveBibsInFront';
        ReserveBibsInFront.FieldName = 'ReserveBibsInFront';
        ReserveBibsInFront.DefinedFor = 'Both';
        ReserveBibsInFront.TranslationKey = 'category.reservebibsinfront';
        ReserveBibsInFront.Editable = true;
        ReserveBibsInFront.Filterable = false;
        ReserveBibsInFront.Sortable = true;
        ReserveBibsInFront.CssClasses = '';
        ReserveBibsInFront.value = (category) => category.ReserveBibsInFront;
        ReserveBibsInFront.setValue = (category, value) => (category.ReserveBibsInFront = value);
        ReserveBibsInFront.DetailEditor = markRaw(InputBool);
        ReserveBibsInFront.DetailEditorProps = { label: 'category.reservebibsinfront' };
        ReserveBibsInFront.CellEditor = markRaw(InputBool);
        ReserveBibsInFront.CellEditorProps = {};
        ReserveBibsInFront.CellRenderer = markRaw(DisplayBool);
        ReserveBibsInFront.CellRendererProps = {};
        ReserveBibsInFront.Validator = null;
        ReserveBibsInFront.SortDirection = undefined;
        ReserveBibsInFront.cellClassRules = undefined;
        ReserveBibsInFront.MinWidth = undefined;
        ReserveBibsInFront.MaxWidth = undefined;
        ReserveBibsInFront.Tooltip = 'category.reservebibsinfronttooltip';
        this.Fields.push(ReserveBibsInFront);

        const separator5 = new FieldDef<Category>();
        separator5.Collapsable = new CollapsableDef('advanced', 'contestant.advanced');
        separator5.Name = 'separator5';
        separator5.FieldName = 'separator5';
        separator5.DefinedFor = 'Detail';
        separator5.TranslationKey = 'category.startsettings';
        separator5.Editable = true;
        separator5.Filterable = false;
        separator5.Sortable = true;
        separator5.CssClasses = '';
        separator5.value = () => {
            return 'category.startsettings';
        };
        separator5.setValue = () => {};
        separator5.DetailEditor = markRaw(DisplaySeparator);
        separator5.DetailEditorProps = {};
        separator5.CellEditor = markRaw(DisplaySeparator);
        separator5.CellEditorProps = {};
        separator5.CellRenderer = markRaw(DisplaySeparator);
        separator5.CellRendererProps = {};
        separator5.Validator = null;
        separator5.SortDirection = undefined;
        separator5.cellClassRules = undefined;
        separator5.MinWidth = undefined;
        separator5.MaxWidth = undefined;
        separator5.Tooltip = null;
        this.Fields.push(separator5);

        const StartTime = new FieldDef<Category>();
        StartTime.Collapsable = new CollapsableDef('advanced', 'contestant.advanced');
        StartTime.Name = 'StartTime';
        StartTime.FieldName = 'StartTime';
        StartTime.DefinedFor = 'Both';
        StartTime.TranslationKey = 'category.starttime';
        StartTime.Editable = true;
        StartTime.Filterable = true;
        StartTime.Sortable = true;
        StartTime.CssClasses = '';
        StartTime.value = (category) => category.StartTime;
        StartTime.setValue = (category, value) => (category.StartTime = value);
        StartTime.DetailEditor = markRaw(InputNullableDateTime);
        StartTime.DetailEditorProps = { format: 'DATETIME' as DateType };
        StartTime.CellEditor = markRaw(InputNullableDateTime);
        StartTime.CellEditorProps = { format: 'DATETIME' as DateType };
        StartTime.CellRenderer = markRaw(DateComp);
        StartTime.CellRendererProps = { format: 'DATETIME' as DateType };
        StartTime.Validator = null;
        StartTime.SortDirection = undefined;
        StartTime.cellClassRules = undefined;
        StartTime.MinWidth = 180;
        StartTime.MaxWidth = undefined;
        StartTime.Tooltip = null;
        this.Fields.push(StartTime);

        const SimultaneousStarts = new FieldDef<Category>();
        SimultaneousStarts.Group = new GroupDef('row35', '', true);
        SimultaneousStarts.Collapsable = new CollapsableDef('advanced', 'contestant.advanced');
        SimultaneousStarts.Name = 'SimultaneousStarts';
        SimultaneousStarts.FieldName = 'SimultaneousStarts';
        SimultaneousStarts.DefinedFor = 'Both';
        SimultaneousStarts.TranslationKey = 'category.simultaneousstarts';
        SimultaneousStarts.Editable = true;
        SimultaneousStarts.Filterable = false;
        SimultaneousStarts.Sortable = true;
        SimultaneousStarts.CssClasses = '';
        SimultaneousStarts.value = (category) => category.SimultaneousStarts;
        SimultaneousStarts.setValue = (category, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            category.SimultaneousStarts = value;
        };
        SimultaneousStarts.DetailEditor = markRaw(InputNullableInt);
        SimultaneousStarts.DetailEditorProps = {};
        SimultaneousStarts.CellEditor = markRaw(InputNullableInt);
        SimultaneousStarts.CellEditorProps = {};
        SimultaneousStarts.CellRenderer = markRaw(ValidatedInt);
        SimultaneousStarts.CellRendererProps = {};
        SimultaneousStarts.Validator = null;
        SimultaneousStarts.SortDirection = undefined;
        SimultaneousStarts.cellClassRules = undefined;
        SimultaneousStarts.MinWidth = undefined;
        SimultaneousStarts.MaxWidth = undefined;
        SimultaneousStarts.Tooltip = 'category.simultaneousstarttooltip';
        this.Fields.push(SimultaneousStarts);

        const infotext6 = new FieldDef<Category>();
        infotext6.Group = new GroupDef('row35', '', true);
        infotext6.Collapsable = new CollapsableDef('advanced', 'contestant.advanced');
        infotext6.Name = 'infotext6';
        infotext6.FieldName = 'infotext6';
        infotext6.DefinedFor = 'Detail';
        infotext6.TranslationKey = '';
        infotext6.Editable = true;
        infotext6.Filterable = false;
        infotext6.Sortable = true;
        infotext6.CssClasses = '';
        infotext6.value = () => {
            return '';
        };
        infotext6.setValue = () => {};
        infotext6.DetailEditor = markRaw(DisplayInfotext);
        infotext6.DetailEditorProps = {};
        infotext6.Validator = null;
        infotext6.SortDirection = undefined;
        infotext6.cellClassRules = undefined;
        infotext6.MinWidth = undefined;
        infotext6.MaxWidth = undefined;
        infotext6.Tooltip = null;
        this.Fields.push(infotext6);

        const infotext7 = new FieldDef<Category>();
        infotext7.Group = new GroupDef('row35', '', true);
        infotext7.Collapsable = new CollapsableDef('advanced', 'contestant.advanced');
        infotext7.Name = 'infotext7';
        infotext7.FieldName = 'infotext7';
        infotext7.DefinedFor = 'Detail';
        infotext7.TranslationKey = '';
        infotext7.Editable = true;
        infotext7.Filterable = false;
        infotext7.Sortable = true;
        infotext7.CssClasses = '';
        infotext7.value = () => {
            return '';
        };
        infotext7.setValue = () => {};
        infotext7.DetailEditor = markRaw(DisplayInfotext);
        infotext7.DetailEditorProps = {};
        infotext7.Validator = null;
        infotext7.SortDirection = undefined;
        infotext7.cellClassRules = undefined;
        infotext7.MinWidth = undefined;
        infotext7.MaxWidth = undefined;
        infotext7.Tooltip = null;
        this.Fields.push(infotext7);

        const infotext8 = new FieldDef<Category>();
        infotext8.Group = new GroupDef('row35', '', true);
        infotext8.Collapsable = new CollapsableDef('advanced', 'contestant.advanced');
        infotext8.Name = 'infotext8';
        infotext8.FieldName = 'infotext8';
        infotext8.DefinedFor = 'Detail';
        infotext8.TranslationKey = '';
        infotext8.Editable = true;
        infotext8.Filterable = false;
        infotext8.Sortable = true;
        infotext8.CssClasses = '';
        infotext8.value = () => {
            return '';
        };
        infotext8.setValue = () => {};
        infotext8.DetailEditor = markRaw(DisplayInfotext);
        infotext8.DetailEditorProps = {};
        infotext8.Validator = null;
        infotext8.SortDirection = undefined;
        infotext8.cellClassRules = undefined;
        infotext8.MinWidth = undefined;
        infotext8.MaxWidth = undefined;
        infotext8.Tooltip = null;
        this.Fields.push(infotext8);

        const StartInterval = new FieldDef<Category>();
        StartInterval.Group = new GroupDef('row40', '', true);
        StartInterval.Collapsable = new CollapsableDef('advanced', 'contestant.advanced');
        StartInterval.Name = 'StartInterval';
        StartInterval.FieldName = 'StartInterval';
        StartInterval.DefinedFor = 'Both';
        StartInterval.TranslationKey = 'category.startinterval';
        StartInterval.Editable = true;
        StartInterval.Filterable = true;
        StartInterval.Sortable = true;
        StartInterval.CssClasses = '';
        StartInterval.value = (category) => category.StartInterval;
        StartInterval.setValue = (category, value) => (category.StartInterval = value);
        StartInterval.DetailEditor = markRaw(InputNullableTimespan);
        StartInterval.DetailEditorProps = { format: 'TIME' as DateType };
        StartInterval.CellEditor = markRaw(InputNullableTimespan);
        StartInterval.CellEditorProps = { format: 'TIME' as DateType };
        StartInterval.CellRenderer = markRaw(DisplayTimespan);
        StartInterval.CellRendererProps = { format: 'TIME' as DateType };
        StartInterval.Validator = null;
        StartInterval.SortDirection = undefined;
        StartInterval.cellClassRules = undefined;
        StartInterval.MinWidth = 100;
        StartInterval.MaxWidth = undefined;
        StartInterval.Tooltip = null;
        this.Fields.push(StartInterval);

        const StartDelay = new FieldDef<Category>();
        StartDelay.Group = new GroupDef('row40', '', true);
        StartDelay.Collapsable = new CollapsableDef('advanced', 'contestant.advanced');
        StartDelay.Name = 'StartDelay';
        StartDelay.FieldName = 'StartDelay';
        StartDelay.DefinedFor = 'Both';
        StartDelay.TranslationKey = 'category.startdelay';
        StartDelay.Editable = true;
        StartDelay.Filterable = true;
        StartDelay.Sortable = true;
        StartDelay.CssClasses = '';
        StartDelay.value = (category) => category.StartDelay;
        StartDelay.setValue = (category, value) => (category.StartDelay = value);
        StartDelay.DetailEditor = markRaw(InputNullableTimespan);
        StartDelay.DetailEditorProps = { format: 'TIME' as DateType };
        StartDelay.CellEditor = markRaw(InputNullableTimespan);
        StartDelay.CellEditorProps = { format: 'TIME' as DateType };
        StartDelay.CellRenderer = markRaw(DisplayTimespan);
        StartDelay.CellRendererProps = { format: 'TIME' as DateType };
        StartDelay.Validator = null;
        StartDelay.SortDirection = undefined;
        StartDelay.cellClassRules = undefined;
        StartDelay.MinWidth = 100;
        StartDelay.MaxWidth = undefined;
        StartDelay.Tooltip = null;
        this.Fields.push(StartDelay);

        const separator9 = new FieldDef<Category>();
        separator9.Collapsable = new CollapsableDef('advanced', 'contestant.advanced');
        separator9.Name = 'separator9';
        separator9.FieldName = 'separator9';
        separator9.DefinedFor = 'Detail';
        separator9.TranslationKey = 'category.resultsettings';
        separator9.Editable = true;
        separator9.Filterable = false;
        separator9.Sortable = true;
        separator9.CssClasses = '';
        separator9.value = () => {
            return 'category.resultsettings';
        };
        separator9.setValue = () => {};
        separator9.DetailEditor = markRaw(DisplaySeparator);
        separator9.DetailEditorProps = {};
        separator9.CellEditor = markRaw(DisplaySeparator);
        separator9.CellEditorProps = {};
        separator9.CellRenderer = markRaw(DisplaySeparator);
        separator9.CellRendererProps = {};
        separator9.Validator = null;
        separator9.SortDirection = undefined;
        separator9.cellClassRules = undefined;
        separator9.MinWidth = undefined;
        separator9.MaxWidth = undefined;
        separator9.Tooltip = null;
        this.Fields.push(separator9);

        const Ranking = new FieldDef<Category>();
        Ranking.Group = new GroupDef('row50', '', true);
        Ranking.Collapsable = new CollapsableDef('advanced', 'contestant.advanced');
        Ranking.Name = 'Ranking';
        Ranking.FieldName = 'Ranking';
        Ranking.DefinedFor = 'Both';
        Ranking.TranslationKey = 'category.ranking';
        Ranking.Editable = true;
        Ranking.Filterable = false;
        Ranking.Sortable = true;
        Ranking.CssClasses = '';
        Ranking.value = (category) => category.Ranking;
        Ranking.setValue = (category, value) => (category.Ranking = value);
        Ranking.DetailEditor = markRaw(InputBool);
        Ranking.DetailEditorProps = { label: 'category.ranking' };
        Ranking.CellEditor = markRaw(InputBool);
        Ranking.CellEditorProps = {};
        Ranking.CellRenderer = markRaw(DisplayBool);
        Ranking.CellRendererProps = {};
        Ranking.Validator = null;
        Ranking.SortDirection = undefined;
        Ranking.cellClassRules = undefined;
        Ranking.MinWidth = undefined;
        Ranking.MaxWidth = undefined;
        Ranking.Tooltip = null;
        this.Fields.push(Ranking);

        const DisplayTimes = new FieldDef<Category>();
        DisplayTimes.Group = new GroupDef('row50', '', true);
        DisplayTimes.Collapsable = new CollapsableDef('advanced', 'contestant.advanced');
        DisplayTimes.Name = 'DisplayTimes';
        DisplayTimes.FieldName = 'DisplayTimes';
        DisplayTimes.DefinedFor = 'Both';
        DisplayTimes.TranslationKey = 'category.displaytimes';
        DisplayTimes.Editable = true;
        DisplayTimes.Filterable = false;
        DisplayTimes.Sortable = true;
        DisplayTimes.CssClasses = '';
        DisplayTimes.value = (category) => category.DisplayTimes;
        DisplayTimes.setValue = (category, value) => (category.DisplayTimes = value);
        DisplayTimes.DetailEditor = markRaw(InputBool);
        DisplayTimes.DetailEditorProps = { label: 'category.displaytimes' };
        DisplayTimes.CellEditor = markRaw(InputBool);
        DisplayTimes.CellEditorProps = {};
        DisplayTimes.CellRenderer = markRaw(DisplayBool);
        DisplayTimes.CellRendererProps = {};
        DisplayTimes.Validator = null;
        DisplayTimes.SortDirection = undefined;
        DisplayTimes.cellClassRules = undefined;
        DisplayTimes.MinWidth = undefined;
        DisplayTimes.MaxWidth = undefined;
        DisplayTimes.Tooltip = null;
        this.Fields.push(DisplayTimes);
    }

    public async LoadDependencies() {
        await useCommonStore().EnsureLoaded(['Genders']);
        await useCommonStore().EnsureLoaded(['PrizeShareTypes']);
    }
}
